import {
	EmptyState,
	PageContainer,
	PageHeader,
	StatusBadge,
} from "@client/components/ui";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@client/components/ui/dropdown-menu";
import { FaviconDisplay } from "@client/components/ui/favicon-display";
import { useUserLimits } from "@client/hooks/useUserLimits";
import { queryUtils, trpc } from "@client/lib/trpc-client";
import { cn } from "@client/lib/utils";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	ChevronLeft,
	ChevronRight,
	Clock,
	Edit,
	FileText,
	Globe,
	MoreVertical,
	PlusCircle,
	Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DeleteSiteDialog } from "../-components/delete-site-dialog";
import { EditSiteDialog } from "../-components/edit-site-dialog";
import { SiteListSkeleton } from "../-components/site-list-skeleton";

const PAGE_SIZE = 12; // Sites per page

export const Route = createFileRoute("/(protected)/sites/")({
	component: SitesPage,
	// Kick off the first page + usage counters while the route chunk loads.
	loader: () => {
		void queryUtils.sites.getRecent.prefetch(
			{ limit: PAGE_SIZE, cursor: 0 },
			{ meta: { silentError: true } },
		);
		void queryUtils.sites.list.prefetch(undefined, {
			meta: { silentError: true },
		});
	},
});

function SitesPage() {
	const [page, setPage] = useState(0);
	const pageSize = PAGE_SIZE;
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [siteToDelete, setSiteToDelete] = useState<string | null>(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [siteToEdit, setSiteToEdit] = useState<{
		id: string;
		url: string;
		name: string;
	} | null>(null);
	const navigate = useNavigate();

	const sites = trpc.sites.getRecent.useQuery(
		{
			limit: pageSize,
			cursor: page * pageSize,
		},
		{
			// Keep the current page on screen while the next one loads instead of
			// flashing the skeleton on every page change.
			placeholderData: keepPreviousData,
			meta: { silentError: true },
		},
	);

	const { atSiteLimit, siteCount, planSiteLimit } = useUserLimits();

	const utils = trpc.useUtils();

	// Prefetch the next page as soon as the current one settles — clicking
	// "Next" then reads straight from cache.
	const nextCursor = sites.data?.nextCursor;
	useEffect(() => {
		if (typeof nextCursor === "number") {
			utils.sites.getRecent.prefetch({ limit: pageSize, cursor: nextCursor });
		}
	}, [nextCursor, pageSize, utils]);

	const deleteMutation = trpc.sites.delete.useMutation({
		onSuccess: (_data, variables) => {
			toast.success("Site deleted successfully");
			// Refresh every cached page of the grid plus the counts behind
			// useUserLimits, and drop the deleted site's detail cache.
			utils.sites.getRecent.invalidate();
			utils.sites.list.invalidate();
			utils.sites.getCount.invalidate();
			utils.sites.getById.reset({ id: variables.id });
			setDeleteDialogOpen(false);
			setSiteToDelete(null);
		},
		onError: (error) => {
			toast.error(error.message || "Failed to delete site");
			setDeleteDialogOpen(false);
			setSiteToDelete(null);
		},
	});

	const updateMutation = trpc.sites.update.useMutation({
		onSuccess: (_data, variables) => {
			toast.success("Site updated successfully");
			utils.sites.getRecent.invalidate();
			utils.sites.list.invalidate();
			utils.sites.getById.invalidate({ id: variables.id });
			setEditDialogOpen(false);
			setSiteToEdit(null);
		},
		onError: (error) => {
			toast.error(error.message || "Failed to update site");
			setEditDialogOpen(false);
			setSiteToEdit(null);
		},
	});

	const totalPages = sites.data ? Math.ceil(sites.data.count / pageSize) : 0;

	const handleEditSite = (siteId: string, currentUrl: string) => {
		const siteName = new URL(currentUrl).hostname;
		setSiteToEdit({ id: siteId, url: currentUrl, name: siteName });
		setEditDialogOpen(true);
	};

	const handleGenerateSite = (siteId: string, siteUrl: string) => {
		navigate({
			to: "/sites/new",
			search: { url: siteUrl, showOptions: true, siteId: siteId },
		});
	};

	const handleDeleteSite = (siteId: string) => {
		setSiteToDelete(siteId);
		setDeleteDialogOpen(true);
	};

	const confirmDelete = () => {
		if (siteToDelete) {
			deleteMutation.mutate({ id: siteToDelete });
		}
	};

	const confirmEdit = (data: { url: string }) => {
		if (siteToEdit) {
			updateMutation.mutate({ id: siteToEdit.id, data });
		}
	};

	const nextPage = () => {
		if (sites.data && sites.data.nextCursor !== null) {
			setPage((p) => p + 1);
		}
	};

	const prevPage = () => {
		if (page > 0) {
			setPage((p) => p - 1);
		}
	};

	const getStatusBadgeVariant = (
		status: string,
	): "active" | "pending" | "failed" => {
		switch (status) {
			case "active":
				return "active";
			case "pending":
				return "pending";
			default:
				return "failed";
		}
	};

	return (
		<PageContainer maxWidth="full">
			<PageHeader
				title="My Sites"
				description="Manage your websites and llms.txt files"
				actions={
					!atSiteLimit ? (
						<Button asChild>
							<Link to="/sites/new">
								<PlusCircle className="mr-2 h-4 w-4" />
								Add Site
							</Link>
						</Button>
					) : (
						<div className="flex items-center gap-2">
							<Button asChild size="sm" variant="secondary">
								<Link to="/settings/billing">Upgrade Plan</Link>
							</Button>
						</div>
					)
				}
			/>

			{/* Site Usage Card */}
			{sites.data && (
				<Card className="mb-6 border-2 border-blue-200 dark:border-blue-900">
					<CardContent className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-950/50">
								<Globe className="h-4 w-4 text-blue-600 dark:text-blue-400" />
							</div>
							<div>
								<p className="font-medium text-sm dark:text-gray-200">
									Site Usage
								</p>
								<p className="text-muted-foreground text-sm dark:text-gray-400">
									<span
										className={`font-medium ${atSiteLimit ? "text-destructive" : "text-foreground dark:text-gray-200"}`}
									>
										{siteCount} of {planSiteLimit}
									</span>{" "}
									sites used
								</p>
							</div>
						</div>
						{atSiteLimit && (
							<StatusBadge variant="pending">At Limit</StatusBadge>
						)}
					</CardContent>
				</Card>
			)}

			{sites.isPending ? (
				<SiteListSkeleton count={6} />
			) : sites.isError ? (
				<div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center dark:border-red-900 dark:bg-red-950/30">
					<h3 className="mb-2 font-semibold text-lg text-red-900 dark:text-red-200">
						Error loading sites
					</h3>
					<p className="mb-4 text-red-800 text-sm dark:text-red-300">
						There was a problem loading your sites.
					</p>
					<Button onClick={() => sites.refetch()} variant="secondary">
						Try Again
					</Button>
				</div>
			) : !sites.data?.sites || sites.data.sites.length === 0 ? (
				<EmptyState
					icon={Globe}
					title={atSiteLimit ? "Site limit reached" : "No sites yet"}
					description={
						atSiteLimit
							? "You've reached your plan's site limit. Upgrade to add more sites."
							: "Add your first site to start generating llms.txt files."
					}
					action={
						!atSiteLimit
							? {
									label: "Add Site",
									onClick: () => navigate({ to: "/sites/new" }),
									icon: PlusCircle,
								}
							: {
									label: "Upgrade Plan",
									onClick: () => navigate({ to: "/settings/billing" }),
									icon: PlusCircle,
								}
					}
				/>
			) : (
				<>
					<div
						className={cn(
							"grid grid-cols-1 gap-4 transition-opacity md:grid-cols-2 lg:grid-cols-3",
							// Dim the previous page while the next one is in flight
							sites.isPlaceholderData && "opacity-60",
						)}
					>
						{sites.data?.sites.map((site) => (
							<Card
								key={site.id}
								className="group relative h-full border-2 transition-all hover:shadow-md dark:border-gray-700"
							>
								{/* Dropdown Menu - positioned absolutely */}
								<div className="absolute top-3 right-3 z-10">
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8 rounded-full opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-800"
												onClick={(e) => e.stopPropagation()}
											>
												<MoreVertical className="h-4 w-4" />
												<span className="sr-only">Site actions</span>
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end" sideOffset={8}>
											<DropdownMenuItem
												onClick={(e) => {
													e.stopPropagation();
													handleEditSite(site.id, site.url);
												}}
											>
												<Edit className="mr-2 h-4 w-4" />
												Edit
											</DropdownMenuItem>
											<DropdownMenuItem
												onClick={(e) => {
													e.stopPropagation();
													handleGenerateSite(site.id, site.url);
												}}
											>
												<FileText className="mr-2 h-4 w-4" />
												Generate
											</DropdownMenuItem>
											<DropdownMenuItem
												onClick={(e) => {
													e.stopPropagation();
													handleDeleteSite(site.id);
												}}
												variant="destructive"
											>
												<Trash2 className="mr-2 h-4 w-4" />
												Delete
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								</div>

								{/* Clickable card content */}
								<Link
									to={"/sites/$siteId"}
									params={{ siteId: site.id }}
									search={{ jobId: undefined }}
									className="block h-full"
								>
									<CardHeader>
										<div className="flex items-center gap-2 pr-10">
											<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
												<FaviconDisplay
													url={site.url}
													size={24}
													className="h-6 w-6 text-primary"
												/>
											</div>
											<div className="min-w-0 flex-1">
												{/* `truncate`, not `line-clamp-1`: a long hostname has no
												    spaces to wrap on, so clamping alone lets it overflow */}
												<CardTitle
													className="truncate text-lg dark:text-gray-100"
													title={new URL(site.url).hostname}
												>
													{new URL(site.url).hostname}
												</CardTitle>
											</div>
										</div>
										<CardDescription
											className="truncate dark:text-gray-400"
											title={site.url}
										>
											{site.url}
										</CardDescription>
									</CardHeader>
									<CardContent>
										<div className="flex items-center gap-2">
											<StatusBadge variant={getStatusBadgeVariant(site.status)}>
												{site.status.charAt(0).toUpperCase() +
													site.status.slice(1)}
											</StatusBadge>
										</div>
									</CardContent>
									<CardFooter>
										<div className="flex items-center text-muted-foreground text-sm dark:text-gray-400">
											<Clock className="mr-1 h-3 w-3" />
											Updated{" "}
											{new Date(site.updatedAt).toLocaleDateString(undefined, {
												year: "numeric",
												month: "short",
												day: "numeric",
											})}
										</div>
									</CardFooter>
								</Link>
							</Card>
						))}
					</div>

					{totalPages > 1 && (
						<Card className="mt-6 border-2 dark:border-gray-700">
							<CardContent className="flex items-center justify-center gap-4 py-4">
								<Button
									variant="outline"
									size="sm"
									onClick={prevPage}
									disabled={page === 0 || sites.isFetching}
								>
									<ChevronLeft className="mr-1 h-4 w-4" /> Previous
								</Button>
								<span className="text-muted-foreground text-sm dark:text-gray-300">
									Page {page + 1} of {totalPages}
								</span>
								<Button
									variant="outline"
									size="sm"
									onClick={nextPage}
									disabled={!sites.data?.nextCursor || sites.isFetching}
								>
									Next <ChevronRight className="ml-1 h-4 w-4" />
								</Button>
							</CardContent>
						</Card>
					)}
				</>
			)}

			<DeleteSiteDialog
				isOpen={deleteDialogOpen}
				onOpenChange={setDeleteDialogOpen}
				onConfirm={confirmDelete}
				isDeleting={deleteMutation.isPending}
			/>

			<EditSiteDialog
				isOpen={editDialogOpen}
				onOpenChange={setEditDialogOpen}
				onConfirm={confirmEdit}
				isUpdating={updateMutation.isPending}
				currentUrl={siteToEdit?.url || ""}
				siteName={siteToEdit?.name || ""}
			/>
		</PageContainer>
	);
}
